import { AsyncPipe, CurrencyPipe, isPlatformBrowser, NgClass, NgIf, NgStyle } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
} from '@angular/core';
import { SiteSvgsComponent } from '@curbnturf/custom-svgs';
import {
  AmenityType,
  DEFAULT_IMAGE_URL,
  HelperFunctions,
  IAmenityType,
  IPhoto,
  IRoutePoint,
  ISite,
  ListingPoint,
  User,
} from '@curbnturf/entities';
import { ListingFacade } from '@curbnturf/listing/src/lib/+state/listing.facade';
import { TripPlannerFacade } from '@curbnturf/trip-planner/src/lib/+state/trip-planner.facade';
import { UserFacade } from '@curbnturf/user/src/lib/+state/user.facade';
import { IonicModule } from '@ionic/angular';
import { Observable, Observer, of, Subscription } from 'rxjs';
import { distinctUntilChanged, map, mergeMap, takeWhile } from 'rxjs/operators';

@Component({
  standalone: true,
  selector: 'curbnturf-search-display-card',
  templateUrl: './search-display-card.component.html',
  styleUrls: ['./search-display-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, CurrencyPipe, NgClass, NgIf, NgStyle, IonicModule, SiteSvgsComponent],
})
export class SearchDisplayCardComponent implements OnChanges, OnInit, OnDestroy {
  @Input() point: ListingPoint;
  @Input() selected: boolean;
  @Input() force: boolean = false;
  @Input() defaultPhoto: string;
  @Input() updateRoute: boolean = true;

  @Output() closeBubble: EventEmitter<void> = new EventEmitter<void>();

  name: string;
  elementVisible$: Observable<boolean>;

  photoUrl: string = '';

  user: User;
  sitePosition: number = 0;
  sites: ISite[] = [];

  subscriptions: Subscription[] = [];

  get favorite(): boolean {
    return !!this.user?.listingFavorites?.find((favorite) => favorite.listingId === this.point?.id);
  }

  get addedToTrip(): boolean {
    if (this.trip.length > 0 && this.point?.id) {
      const listingFound = this.trip.find((el) => {
        if (el.isListing && this.point?.id && el.listing?.id) {
          return el.listing?.id === this.point.id;
        }
        return undefined;
      });
      if (listingFound) {
        return true;
      }
    }

    return false;
  }

  private trip: IRoutePoint[] = [];

  constructor(
    private cd: ChangeDetectorRef,
    public element: ElementRef,
    @Inject(PLATFORM_ID) private platformId: string,
    private listingFacade: ListingFacade,
    private tripPlannerFacade: TripPlannerFacade,
    private userFacade: UserFacade,
  ) {
    if (isPlatformBrowser(this.platformId)) {
      this.elementVisible$ = new Observable((observer: Observer<IntersectionObserverEntry[]>) => {
        const intersectionObserver = new IntersectionObserver((entries) => {
          observer.next(entries);
        });
        intersectionObserver.observe(element.nativeElement);
        return () => {
          if (intersectionObserver) {
            intersectionObserver.disconnect();
          }
        };
      }).pipe(
        mergeMap((entries) => entries),
        map((entry: IntersectionObserverEntry): boolean => entry.isIntersecting),
        takeWhile((result) => !result, true),
        distinctUntilChanged(),
      );
    } else {
      this.elementVisible$ = of();
    }
  }

  ngOnInit() {
    this.subscriptions.push(
      this.userFacade.currentUser$.subscribe((user) => {
        this.user = user;
        this.cd.detectChanges();
      }),
      this.tripPlannerFacade.trip$.subscribe((trip) => {
        if (trip && trip.length > 0) {
          this.trip = trip;
        } else {
          this.trip = [];
        }
        this.cd.detectChanges();
      }),
      this.listingFacade.selected$.subscribe((listing) => {
        if (listing && this.point && listing.id === this.point.id) {
          this.sites = [...(listing.sites || [])].sort((siteA, siteB) => {
            if (!siteA?.id) {
              return 1;
            }
            if (!siteB?.id) {
              return -1;
            }
            return siteA.id - siteB.id;
          });

          if (this.sites.length > 0) {
            this.calculateName();
            this.calculatePhoto();
          }

          this.cd.detectChanges();
        } else {
          this.sites = [];
        }
      }),
    );
  }

  ngOnChanges() {
    this.name = '';
    this.photoUrl = '';

    if (this.point) {
      this.calculateName();
      this.calculatePhoto();

      this.listingFacade.select(this.point.id);
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => {
      if (!sub.closed) {
        sub.unsubscribe();
      }
    });
  }

  calculateName() {
    this.name = this.point.getName();

    if (this.point.id !== 1) {
      this.name = this.name.slice(0, 32);
    }

    if (this.sites.length > 1) {
      this.name = `${this.name} - ${this.sites[this.sitePosition].name}`;
    }
  }

  calculatePhoto() {
    if (
      this.sites.length > 1 &&
      this.sites[this.sitePosition]?.photos &&
      (this.sites[this.sitePosition].photos as IPhoto[]).length > 0
    ) {
      this.photoUrl = HelperFunctions.getPhotoUrl3x4Small((this.sites[this.sitePosition].photos as IPhoto[])[0]);
    } else if (this.point.photos && this.point.photos.length > 0) {
      this.photoUrl = HelperFunctions.getPhotoUrl3x4Small(this.point.photos[0]);
    } else {
      this.photoUrl = this.defaultPhoto?.length ? this.defaultPhoto : DEFAULT_IMAGE_URL;
    }
  }

  amenityName(amenity: IAmenityType['type']): string {
    if (!amenity) {
      return '';
    }

    if (amenity) {
      return AmenityType[amenity];
    }

    return '';
  }

  onFavorite(event: Event) {
    event.stopPropagation();

    if (this.favorite) {
      this.listingFacade.unfavorite(new ListingPoint({ ...this.point }));
    } else {
      this.listingFacade.favorite(new ListingPoint({ ...this.point }));
    }
  }

  onAddToTrip(event: Event) {
    const point: IRoutePoint = {
      isListing: true,
      listing: {
        id: this.point.id,
        coordinates: {
          ...this.point.coordinates,
        },
        name: this.point.name,
        address: this.point.address,
        photos: this.point.photos,
        //@todo this should move to a site somehow
        //price: this.point.price,
        rating: this.point.rating,
        ratingCount: this.point.ratingCount,
      },
      name: this.point.name,
      coordinates: { ...this.point.coordinates },
      isActive: true,
    };
    this.tripPlannerFacade.addPoint(point, this.updateRoute);
    this.closeBubble.emit();
  }

  onRemoveFromTrip(event: Event) {
    event.stopPropagation();
    const foundPoint = this.trip.find((el) => el.listing?.id === this.point?.id);
    if (foundPoint) {
      this.tripPlannerFacade.removePoint(foundPoint);
    }
    this.closeBubble.emit();
  }

  sitePrevious(event: Event) {
    event.stopPropagation();

    if (this.sitePosition > 0) {
      this.sitePosition -= 1;

      this.calculateName();
      this.calculatePhoto();
    }
  }

  siteNext(event: Event) {
    event.stopPropagation();

    if (this.sitePosition < this.sites.length - 1) {
      this.sitePosition += 1;

      this.calculateName();
      this.calculatePhoto();
    }
  }
}
